import { useEffect, useState } from "react";
import { Crown, Loader2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/lib/confirm";
import { trpc } from "@/lib/trpc";
import { ROLE_LABEL } from "@/components/team/role-meta";

// ─── Seção "Propriedade" (transferir dono — só visível pro OWNER) ─

export function PropriedadeSecao() {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const meQuery = trpc.me.current.useQuery();
  const activeOrganization = meQuery.data?.activeOrganization;
  const isOwner = activeOrganization?.role === "OWNER";
  const membersQuery = trpc.team.list.useQuery(undefined, { enabled: isOwner });
  const [selected, setSelected] = useState("");
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const initiateTransfer = trpc.organization.initiateOwnershipTransfer.useMutation({
    onSuccess: (data) => {
      setPendingRequestId(data.requestId);
      setCode("");
      setResendCooldown(30);
      toast.success("Enviamos um código de confirmação pro seu e-mail.");
    },
    onError: (err) => toast.error(err.message),
  });

  const confirmTransfer = trpc.organization.confirmOwnershipTransfer.useMutation({
    onSuccess: () => {
      utils.team.list.invalidate();
      utils.me.current.invalidate();
      toast.success("Propriedade transferida — você agora é Administrador.");
      setSelected("");
      setPendingRequestId(null);
      setCode("");
    },
    onError: (err) => toast.error(err.message),
  });

  if (activeOrganization?.isPersonal) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-primary/10 dark:text-primary">
          <UserIcon className="h-6 w-6" />
        </div>
        <h3 className="max-w-md text-sm font-semibold text-zinc-900 dark:text-foreground">
          Esta é sua organização pessoal
        </h3>
        <p className="max-w-md text-[12.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
          Organizações pessoais têm um único dono e não têm o que transferir.
        </p>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">
          <Crown className="h-6 w-6" />
        </div>
        <h3 className="max-w-md text-sm font-semibold text-zinc-900 dark:text-foreground">
          Só o proprietário pode transferir a propriedade
        </h3>
        <p className="max-w-md text-[12.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
          Fale com o proprietário atual da organização se precisar assumir essa função.
        </p>
      </div>
    );
  }

  const candidates = (membersQuery.data ?? []).filter(
    (m: any) => m.role !== "OWNER" && m.status !== "SUSPENDED",
  );
  const selectedMember = candidates.find((m: any) => m.id === selected);

  const handleTransfer = async () => {
    if (!selectedMember) return;
    const name = selectedMember.user.name || selectedMember.user.email;
    const ok = await confirm({
      title: "Transferir propriedade?",
      description: `${name} vira o(a) novo(a) proprietário(a) e você passa a Administrador. Vamos enviar um código de confirmação pro seu e-mail antes de concluir. Só quem vira dono pode devolver o cargo depois.`,
      confirmText: "Continuar",
      destructive: true,
    });
    if (ok) initiateTransfer.mutate({ toMembershipId: selectedMember.id });
  };

  const handleCancelCode = () => {
    setPendingRequestId(null);
    setCode("");
  };

  const handleConfirmCode = () => {
    if (!pendingRequestId || code.length !== 6) return;
    confirmTransfer.mutate({ requestId: pendingRequestId, code });
  };

  const handleResend = () => {
    if (!selectedMember || resendCooldown > 0) return;
    initiateTransfer.mutate({ toMembershipId: selectedMember.id });
  };

  if (pendingRequestId) {
    return (
      <div className="space-y-4 px-6 py-5">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
            Confirme com o código enviado pro seu e-mail
          </h3>
          <p className="mt-1 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            Enviamos um código de 6 dígitos pro seu próprio e-mail cadastrado — digite ele abaixo
            pra concluir a transferência pra {selectedMember?.user.name || selectedMember?.user.email}.
            O código expira em 10 minutos.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            className="h-9 w-[140px] rounded-md border border-zinc-200 bg-white px-3 text-center text-[15px] font-semibold tracking-[0.3em] text-zinc-900 dark:border-border dark:bg-background dark:text-foreground"
          />
          <button
            type="button"
            onClick={handleConfirmCode}
            disabled={code.length !== 6 || confirmTransfer.isPending}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-amber-600 px-4 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-amber-700 disabled:opacity-50"
          >
            {confirmTransfer.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <Crown className="h-3.5 w-3.5" />
            Confirmar
          </button>
          <button
            type="button"
            onClick={handleCancelCode}
            disabled={confirmTransfer.isPending}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-4 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Cancelar
          </button>
        </div>

        <button
          type="button"
          onClick={handleResend}
          disabled={resendCooldown > 0 || initiateTransfer.isPending}
          className="text-[12.5px] font-medium text-brand-700 transition-colors hover:underline disabled:cursor-not-allowed disabled:text-zinc-400 disabled:no-underline dark:text-primary dark:disabled:text-muted-foreground"
        >
          {resendCooldown > 0 ? `Reenviar código (${resendCooldown}s)` : "Reenviar código"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-6 py-5">
      <div>
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
          Transferir propriedade da organização
        </h3>
        <p className="mt-1 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
          Escolha um membro ativo da equipe para assumir a propriedade. Você passa a
          Administrador automaticamente. Vamos pedir um código de confirmação enviado pro seu
          e-mail antes de concluir — essa ação é sensível.
        </p>
      </div>

      {membersQuery.isLoading ? (
        <p className="text-sm text-zinc-500 dark:text-muted-foreground">Carregando equipe...</p>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-zinc-500 dark:text-muted-foreground">
          Nenhum outro membro ativo na equipe para transferir a propriedade.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2.5">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="h-9 min-w-[240px] rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 dark:border-border dark:bg-background dark:text-foreground"
          >
            <option value="">Selecione um membro</option>
            {candidates.map((m: any) => (
              <option key={m.id} value={m.id}>
                {m.user.name || m.user.email} — {ROLE_LABEL[m.role] ?? m.role}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleTransfer}
            disabled={!selected || initiateTransfer.isPending}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-amber-600 px-4 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-amber-700 disabled:opacity-50"
          >
            {initiateTransfer.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <Crown className="h-3.5 w-3.5" />
            Transferir propriedade
          </button>
        </div>
      )}
    </div>
  );
}
